import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Save, RefreshCw } from 'lucide-react';
import { settingsApi, syncApi } from '@/services/api';
import type { Settings as SettingsType, SyncStatus } from '@/types';

export function Settings() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [localSettings, setLocalSettings] = useState<Record<string, unknown>>({});

  const { data: settings, isLoading } = useQuery<{ settings: SettingsType }>({
    queryKey: ['settings'],
    queryFn: settingsApi.getAll,
  });

  const { data: syncStatus } = useQuery<SyncStatus>({
    queryKey: ['sync-status'],
    queryFn: syncApi.getStatus,
    refetchInterval: 10000,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => settingsApi.bulkUpdate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: (type: 'full' | 'incremental') => syncApi.trigger(type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sync-status'] });
    },
  });

  const handleSave = () => {
    updateMutation.mutate(localSettings);
  };

  const getValue = (key: string) => {
    if (localSettings[key] !== undefined) return localSettings[key];
    return (settings?.settings as any)?.[key]?.value;
  };

  const setValue = (key: string, value: unknown) => {
    setLocalSettings((prev) => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
        <button
          onClick={handleSave}
          disabled={updateMutation.isPending || Object.keys(localSettings).length === 0}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Save className="w-4 h-4" />
          {t('common.save')}
        </button>
      </div>

      {/* Sync Status */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('sync.title')}</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">{t('sync.lastSync')}</span>
            <span className="font-medium">
              {syncStatus?.lastSync?.completedAt
                ? new Date(syncStatus.lastSync.completedAt).toLocaleString()
                : '-'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">{t('sync.nextSync')}</span>
            <span className="font-medium">
              {syncStatus?.nextSync
                ? new Date(syncStatus.nextSync).toLocaleString()
                : '-'}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => syncMutation.mutate('incremental')}
              disabled={syncMutation.isPending || syncStatus?.isRunning}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${syncStatus?.isRunning ? 'animate-spin' : ''}`} />
              {t('sync.incremental')}
            </button>
            <button
              onClick={() => syncMutation.mutate('full')}
              disabled={syncMutation.isPending || syncStatus?.isRunning}
              className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${syncStatus?.isRunning ? 'animate-spin' : ''}`} />
              {t('sync.full')}
            </button>
          </div>
        </div>
      </div>

      {/* Settings Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('settings.title')}</h2>
        <div className="space-y-6">
          {/* Sync Interval */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.syncInterval')}
            </label>
            <input
              type="number"
              min="1"
              max="60"
              value={getValue('syncIntervalMinutes') || 1}
              onChange={(e) => setValue('syncIntervalMinutes', parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Fireworks Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.fireworksDuration')}
            </label>
            <input
              type="number"
              min="1"
              max="30"
              value={getValue('fireworksDurationSeconds') || 10}
              onChange={(e) => setValue('fireworksDurationSeconds', parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* WhatsApp Template */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.whatsappTemplate')}
            </label>
            <input
              type="text"
              value={getValue('whatsappTemplateName') || ''}
              onChange={(e) => setValue('whatsappTemplateName', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Manager Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.managerPhone')}
            </label>
            <input
              type="text"
              placeholder="972548121042"
              value={
                Array.isArray(getValue('managerWhatsappNumbers'))
                  ? (getValue('managerWhatsappNumbers') as string[]).join(', ')
                  : getValue('managerWhatsappNumbers') || ''
              }
              onChange={(e) => {
                const phones = e.target.value
                  .split(',')
                  .map((p) => p.trim())
                  .filter((p) => p.length > 0);
                setValue('managerWhatsappNumbers', phones);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">{t('settings.managerPhoneHint')}</p>
          </div>

          {/* Celebration Enabled */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              {t('settings.celebrationEnabled')}
            </label>
            <input
              type="checkbox"
              checked={getValue('celebrationEnabled') ?? true}
              onChange={(e) => setValue('celebrationEnabled', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </div>

          {/* WhatsApp Enabled */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              {t('settings.whatsappEnabled')}
            </label>
            <input
              type="checkbox"
              checked={getValue('whatsappNotificationsEnabled') ?? true}
              onChange={(e) => setValue('whatsappNotificationsEnabled', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
